import React, { useState, useCallback, useEffect, useRef } from 'react';

// Werkzeug-spezifische Hilfe-Texte
const TOOL_HELP = {
  select: 'Klick = Auswählen | Shift+Klick = Hinzufügen | Ziehen = Rechteck-Selektion',
  point: 'Klick = Punkt | Auf Punkt = Referenz | Alt = Ohne Wandteilung',
  wall: 'Klick auf 2 Punkte = Wand verbinden',
  arc: '3 Punkte: Start → Scheitel → Ende | Orangen Punkt ziehen = Bogen ändern',
  space: 'Punkte nacheinander anklicken → Enter = Raum erstellen | Escape = Abbrechen',
  shaft: 'Mind. 3 Punkte anklicken → Enter = Schacht erstellen | Escape = Abbrechen',
  stub_wall: 'Mind. 3 Punkte anklicken → Enter = Vorwand erstellen | Escape = Abbrechen',
  measure: 'Klicken = Messpunkt setzen | Enter = Fertig | Escape = Abbrechen | ≥3 Punkte = Fläche',
  // Geometry tools
  circle: 'Mittelpunkt klicken, dann Radius klicken oder Zahl eingeben',
  rectangle: 'Erste Ecke klicken, dann gegenüber klicken oder B,H eingeben',
  polygon: 'Mittelpunkt klicken, dann Eckpunkt klicken oder Radius eingeben',
  line: 'Startpunkt klicken, dann Endpunkt klicken oder Länge eingeben',
  polyline: 'Punkte klicken, Enter = Abschließen',
  construction_line: 'Zwei Punkte für Hilfslinie klicken',
  cylinder: 'Basismittelpunkt, dann Radius klicken oder Zahl eingeben',
  sphere: 'Mittelpunkt, dann Radius klicken oder Zahl eingeben',
  box: 'Erste Ecke, dann gegenüber klicken oder B,T eingeben',
};

// Verfügbare Rastergrößen (in mm)
const GRID_SIZES = [
  { value: 5, label: '5mm' },
  { value: 10, label: '10mm' },
  { value: 25, label: '25mm' },
  { value: 50, label: '50mm' },
  { value: 100, label: '10cm' },
  { value: 250, label: '25cm' },
  { value: 500, label: '50cm' },
  { value: 1000, label: '1m' },
];

// Verfügbare Segmentanzahlen für Bögen (von useTools importiert)
const ARC_SEGMENT_OPTIONS = [4, 6, 8, 12, 16, 24, 32];

// Drawing tools that support dimension input
const DRAWING_TOOLS = ['circle', 'rectangle', 'polygon', 'cylinder', 'sphere', 'box', 'line', 'construction_line'];

export function StatusBar({
  mousePosition,
  lastPoint,
  orthoMode,
  gridSnap,
  gridSize,
  osnap,
  activeTool,
  onToggleOrtho,
  onToggleGridSnap,
  onToggleOsnap,
  onGridSizeChange,
  onCoordinateInput,
  onDistanceInput,
  // Punkt-Einstellungen
  pointSettings,
  // Arc-Einstellungen
  arcSegments,
  onArcSegmentsChange,
  // Drawing tools
  drawingTools, // From useDrawingTools hook
  onDrawingDimensionInput, // Callback when dimension is entered for drawing tools
}) {
  const [inputMode, setInputMode] = useState(null); // 'x', 'y', 'xy', 'distance'
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  // Koordinaten formatieren (mm -> m)
  const formatCoord = (val) => {
    if (val == null) return '-.--';
    return (val / 1000).toFixed(2);
  };

  // Distanz und Winkel berechnen
  const relX = lastPoint ? mousePosition.x - lastPoint.x : 0;
  const relY = lastPoint ? mousePosition.y - lastPoint.y : 0;
  const distance = lastPoint ? Math.sqrt(relX * relX + relY * relY) : 0;
  const angle = lastPoint ? Math.atan2(relY, relX) * (180 / Math.PI) : 0;

  // Richtungs-Vektor (normalisiert)
  const dirX = distance > 0 ? relX / distance : 1;
  const dirY = distance > 0 ? relY / distance : 0;

  // Check if current tool is a drawing tool in second-point phase
  const isDrawingToolActive = drawingTools?.isWaitingForSecondPoint && DRAWING_TOOLS.includes(activeTool);

  // Global keydown listener für Zahlen-Eingabe
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Ignorieren wenn bereits in Input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Drawing tools dimension input (when waiting for second point)
      if (isDrawingToolActive) {
        if (/^[0-9\-\.,]$/.test(e.key)) {
          e.preventDefault();
          setInputMode('drawing');
          setInputValue(e.key);
          return;
        } else if (e.key === 'Tab') {
          e.preventDefault();
          setInputMode('drawing');
          setInputValue('');
          return;
        }
      }

      // Zahlen oder Minus -> Distanz-Eingabe starten
      if (lastPoint && (activeTool === 'point' || activeTool === 'wall' || activeTool === 'space')) {
        if (/^[0-9\-\.]$/.test(e.key)) {
          e.preventDefault();
          setInputMode('distance');
          setInputValue(e.key);
        } else if (e.key === 'Tab') {
          e.preventDefault();
          setInputMode('distance');
          setInputValue('');
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [lastPoint, activeTool, isDrawingToolActive]);

  // Focus input wenn Modus wechselt
  useEffect(() => {
    if (inputMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputMode]);

  // Eingabe-Handler
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      const value = inputValue.trim();

      if (inputMode === 'drawing') {
        // Drawing tool dimension input (radius, width, etc.)
        if (onDrawingDimensionInput) {
          onDrawingDimensionInput(value);
        }
        setInputValue('');
        setInputMode(null);
        return;
      } else if (inputMode === 'distance') {
        // Distanz-Eingabe: Punkt in Richtung der Maus setzen
        const dist = parseFloat(value.replace(',', '.')) * 1000; // m -> mm

        if (!isNaN(dist) && lastPoint) {
          // Berechne neue Position in Richtung der Maus
          let newX, newY;

          if (orthoMode) {
            // Im Ortho-Modus: Horizontal oder vertikal
            if (Math.abs(relX) > Math.abs(relY)) {
              // Horizontal
              newX = lastPoint.x + (relX >= 0 ? dist : -dist);
              newY = lastPoint.y;
            } else {
              // Vertikal
              newX = lastPoint.x;
              newY = lastPoint.y + (relY >= 0 ? dist : -dist);
            }
          } else {
            // Freie Richtung
            newX = lastPoint.x + dirX * dist;
            newY = lastPoint.y + dirY * dist;
          }

          onDistanceInput?.({ x: newX, y: newY, distance: dist });
        }
      } else {
        // Koordinaten-Eingabe
        let x, y, isRelative = false;

        if (value.startsWith('@')) {
          isRelative = true;
          const coords = value.slice(1).split(/[,;]/);
          x = parseFloat(coords[0]?.replace(',', '.')) * 1000;
          y = parseFloat(coords[1]?.replace(',', '.')) * 1000;
        } else if (value.includes(',') || value.includes(';')) {
          const coords = value.split(/[,;]/);
          x = parseFloat(coords[0]?.replace(',', '.')) * 1000;
          y = parseFloat(coords[1]?.replace(',', '.')) * 1000;
        } else {
          const num = parseFloat(value.replace(',', '.')) * 1000;
          if (inputMode === 'x') x = num;
          else if (inputMode === 'y') y = num;
          else x = num;
        }

        if (isRelative && lastPoint) {
          if (x != null) x += lastPoint.x;
          if (y != null) y += lastPoint.y;
        }

        onCoordinateInput?.({ x, y });
      }

      setInputValue('');
      setInputMode(null);
    } else if (e.key === 'Escape') {
      setInputValue('');
      setInputMode(null);
    }
  }, [inputValue, inputMode, lastPoint, orthoMode, relX, relY, dirX, dirY, onCoordinateInput, onDistanceInput]);

  // Klick auf Koordinate = Eingabemodus
  const handleCoordClick = (mode) => {
    setInputMode(mode);
    setInputValue('');
  };

  return (
    <div className="absolute bottom-0 left-0 right-0 h-10 bg-gray-800 text-white text-xs flex items-center px-2 gap-3 select-none z-50">
      {/* Koordinaten-Anzeige */}
      <div className="flex items-center gap-2 font-mono">
        {inputMode === 'drawing' ? (
          <div className="flex items-center gap-2">
            <span className="text-cyan-400">{drawingTools?.config?.dimensionLabel || 'Maß'}:</span>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => { setInputMode(null); setInputValue(''); }}
              placeholder={drawingTools?.config?.twoValues ? 'z.B. 2,3' : 'Meter'}
              className="w-24 px-1 bg-gray-700 border border-cyan-500 rounded text-white text-center"
              autoFocus
            />
            {drawingTools?.formattedDimension && (
              <span className="text-gray-400">
                aktuell: {drawingTools.formattedDimension}
              </span>
            )}
          </div>
        ) : inputMode === 'distance' ? (
          <div className="flex items-center gap-2">
            <span className="text-yellow-400">Distanz:</span>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => { setInputMode(null); setInputValue(''); }}
              placeholder="Meter"
              className="w-20 px-1 bg-gray-700 border border-yellow-500 rounded text-white text-center"
            />
            <span className="text-gray-400">
              Richtung: {angle.toFixed(0)}°
            </span>
          </div>
        ) : inputMode ? (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => { setInputMode(null); setInputValue(''); }}
            placeholder={inputMode === 'xy' ? 'X,Y (m)' : `${inputMode.toUpperCase()} (m)`}
            className="w-32 px-1 bg-gray-700 border border-gray-600 rounded text-white"
          />
        ) : (
          <>
            <span
              className="cursor-pointer hover:bg-gray-700 px-1 rounded"
              onClick={() => handleCoordClick('x')}
              title="Klicken für X-Eingabe"
            >
              X: <span className="text-red-400">{formatCoord(mousePosition.x)}</span>
            </span>
            <span
              className="cursor-pointer hover:bg-gray-700 px-1 rounded"
              onClick={() => handleCoordClick('y')}
              title="Klicken für Y-Eingabe"
            >
              Y: <span className="text-green-400">{formatCoord(mousePosition.y)}</span>
            </span>
          </>
        )}
      </div>

      {/* Distanz-Anzeige (wenn lastPoint existiert) */}
      {lastPoint && !inputMode && !isDrawingToolActive && (
        <>
          <div className="h-4 w-px bg-gray-600" />
          <div className="flex items-center gap-2 font-mono text-yellow-400">
            <span title="Distanz vom letzten Punkt">
              D: {formatCoord(distance)}m
            </span>
            <span className="text-gray-500" title="Winkel">
              ∠{angle.toFixed(0)}°
            </span>
          </div>
        </>
      )}

      {/* Drawing tool dimension display */}
      {isDrawingToolActive && !inputMode && drawingTools?.formattedDimension && (
        <>
          <div className="h-4 w-px bg-gray-600" />
          <div className="flex items-center gap-2 font-mono text-cyan-400">
            <span title={drawingTools?.config?.dimensionLabel}>
              {drawingTools?.config?.dimensionLabel?.split(' ')[0]}: {drawingTools.formattedDimension}
            </span>
            <span className="text-gray-500 text-xs">
              (Zahl tippen für exakte Eingabe)
            </span>
          </div>
        </>
      )}

      {/* Trennlinie */}
      <div className="h-4 w-px bg-gray-600" />

      {/* CAD-Modi */}
      <div className="flex items-center gap-1">
        <button
          onClick={onToggleOrtho}
          className={`px-2 py-0.5 rounded ${
            orthoMode
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
          }`}
          title="Ortho-Modus (F8) - Nur 0°/90°"
        >
          ORTHO
        </button>

        <button
          onClick={onToggleGridSnap}
          className={`px-2 py-0.5 rounded-l ${
            gridSnap
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
          }`}
          title="Grid-Snap (F9)"
        >
          GRID
        </button>
        {/* Grid-Größe Auswahl */}
        <select
          value={gridSize}
          onChange={(e) => onGridSizeChange?.(Number(e.target.value))}
          className="px-1 py-0.5 rounded-r bg-gray-700 text-gray-300 text-xs border-l border-gray-600 cursor-pointer hover:bg-gray-600"
          title="Rastergröße"
        >
          {GRID_SIZES.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <button
          onClick={() => onToggleOsnap?.()}
          className={`px-2 py-0.5 rounded ${
            osnap.enabled
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
          }`}
          title="Object Snap (F3)"
        >
          OSNAP
        </button>
      </div>

      {/* Trennlinie */}
      <div className="h-4 w-px bg-gray-600" />

      {/* Aktives Tool */}
      <div className="text-gray-400">
        <span className="text-white uppercase">{activeTool}</span>
      </div>

      {/* Punkt-Einstellungen (nur wenn Punkt-Tool aktiv) */}
      {activeTool === 'point' && pointSettings && (
        <>
          <div className="h-4 w-px bg-gray-600" />
          <div className="flex items-center gap-2">
            {/* Wandtrennend Toggle */}
            <button
              onClick={pointSettings.toggleWallSplitting}
              className={`px-2 py-0.5 rounded text-xs ${
                pointSettings.wallSplitting
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              }`}
              title={pointSettings.wallSplitting
                ? 'Wandtrennend: Klick auf Wand teilt diese'
                : 'Nicht wandtrennend: Punkt ohne Wandteilung'
              }
            >
              {pointSettings.wallSplitting ? 'SPLIT' : 'NO-SPLIT'}
            </button>
          </div>
        </>
      )}

      {/* Arc-Einstellungen (nur wenn Arc-Tool aktiv) */}
      {activeTool === 'arc' && (
        <>
          <div className="h-4 w-px bg-gray-600" />
          <div className="flex items-center gap-2">
            <span className="text-gray-400">Segmente:</span>
            <select
              value={arcSegments || 8}
              onChange={(e) => onArcSegmentsChange?.(Number(e.target.value))}
              className="px-2 py-0.5 bg-gray-700 text-white text-xs border border-gray-600 rounded cursor-pointer hover:bg-gray-600"
              title="Anzahl der Bogensegmente"
            >
              {ARC_SEGMENT_OPTIONS.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span className="text-gray-500 text-xs">
              (mehr = glatter)
            </span>
          </div>
        </>
      )}

      {/* Snap-Info */}
      {osnap.enabled && (
        <div className="flex items-center gap-1 text-gray-500">
          {osnap.end && <span className="text-yellow-500">END</span>}
          {osnap.mid && <span className="text-cyan-500">MID</span>}
        </div>
      )}

      {/* Werkzeug-Hilfe */}
      <div className="ml-auto flex items-center gap-3">
        {/* Werkzeug-spezifischer Hinweis */}
        <span className="text-cyan-400">
          {TOOL_HELP[activeTool] || 'P=Punkt | W=Wand | R=Raum | S=Schacht | T=Vorwand'}
        </span>
        {/* Distanz-Eingabe Hinweis */}
        {lastPoint && (
          <span className="text-yellow-400 border-l border-gray-600 pl-3">
            Zahl = Distanz
          </span>
        )}
      </div>
    </div>
  );
}
